import React from 'react'
import { motion } from 'framer-motion'
import { ExclamationTriangleIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline'
import Button from './Button'
import Modal from './Modal'

interface NoiseConsentModalProps {
  isOpen: boolean
  onClose: () => void
  onAcceptAndProceed: () => void
  onRetry: () => void
  noiseLevel: 'moderate' | 'high'
  recommendation: string
}

const NoiseConsentModal: React.FC<NoiseConsentModalProps> = ({
  isOpen,
  onClose,
  onAcceptAndProceed,
  onRetry,
  noiseLevel,
  recommendation
}) => {
  const isHighNoise = noiseLevel === 'high'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`p-3 rounded-full ${isHighNoise ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <SpeakerWaveIcon className={`h-6 w-6 ${isHighNoise ? 'text-red-600' : 'text-yellow-600'}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isHighNoise ? 'High Background Noise Detected' : 'Background Noise Detected'}
            </h3>
            <p className={`text-sm ${isHighNoise ? 'text-red-600' : 'text-yellow-600'}`}>
              Your environment may affect speech recognition accuracy
            </p>
          </div>
        </div>

        {/* Warning Message */}
        <div className={`p-4 rounded-lg border-l-4 mb-6 ${
          isHighNoise 
            ? 'bg-red-50 border-red-400' 
            : 'bg-yellow-50 border-yellow-400'
        }`}>
          <div className="flex">
            <ExclamationTriangleIcon className={`h-5 w-5 mt-0.5 ${
              isHighNoise ? 'text-red-400' : 'text-yellow-400'
            }`} />
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                isHighNoise ? 'text-red-800' : 'text-yellow-800'
              }`}>
                Environment Assessment Result:
              </p>
              <p className={`text-sm mt-1 ${
                isHighNoise ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {recommendation}
              </p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            To improve speech recognition accuracy:
          </h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Use headphones to reduce background noise pickup</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Move to a quieter location if possible</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Turn off music, TV, or other audio sources</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Consider using text input mode instead</span>
            </li>
          </ul>
        </div>

        {/* Consent Warning */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> By proceeding with speech mode in this environment, 
            you acknowledge that background noise may affect the accuracy of speech recognition. 
            Any technical issues related to audio quality may impact your interview experience.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={onRetry}
            className="sm:order-1"
          >
            Test Again
          </Button>
          
          <Button
            variant="outline"
            onClick={onClose}
            className="sm:order-2"
          >
            Switch to Text Mode
          </Button>
          
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="sm:order-3"
          >
            <Button
              variant={isHighNoise ? "danger" : "primary"}
              onClick={onAcceptAndProceed}
              className="w-full sm:w-auto"
            >
              {isHighNoise 
                ? 'I Accept the Risk & Continue' 
                : 'I Understand & Continue'
              }
            </Button>
          </motion.div>
        </div>
      </div>
    </Modal>
  )
}

export default NoiseConsentModal